/**
 * Button — the only clickable-action element in the console.
 *
 * Variants map to consequence, not to taste:
 *   primary   the one action this view exists for (Run now, Save, Send)
 *   secondary everything else that acts               (default)
 *   ghost     low-weight/inline actions (Copy, toolbar toggles)
 *   danger    destructive and irreversible (Revoke, Delete)
 *
 * `loading` disables the button and swaps in a spinner while keeping the label, so
 * the control never changes width mid-click and nobody double-fires a dispatch.
 */

import { forwardRef } from 'react';
import type { ButtonHTMLAttributes, ReactNode } from 'react';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
export type ButtonSize = 'sm' | 'md';

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className'> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Shows a spinner and blocks clicks. Use for anything that hits the API. */
  loading?: boolean;
  /** Leading glyph or icon element. Keep it to one. */
  icon?: ReactNode;
  /** Icon-only: pass `icon` and set this. `title`/`aria-label` then become mandatory. */
  iconOnly?: boolean;
  /** Fill the container width (modal footers on mobile, sidebars). */
  block?: boolean;
  className?: string;
  children?: ReactNode;
}

const VARIANT_CLASS: Record<ButtonVariant, string> = {
  primary: 'btn-primary',
  secondary: '',
  ghost: 'btn-ghost',
  danger: 'btn-danger',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'secondary',
    size = 'md',
    loading = false,
    icon,
    iconOnly = false,
    block = false,
    disabled,
    type = 'button',
    className = '',
    children,
    ...rest
  },
  ref,
) {
  const classes = [
    'btn',
    VARIANT_CLASS[variant],
    size === 'sm' ? 'btn-sm' : '',
    iconOnly ? 'btn-icon' : '',
    block ? 'btn-block' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <button
      ref={ref}
      type={type}
      className={classes}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading ? <span className="spinner" aria-hidden="true" /> : icon}
      {!iconOnly && children}
    </button>
  );
});

/**
 * A segmented control. Give each child Button `className="is-selected"` when active
 * and `aria-pressed`, e.g. the borrower/detailed shape switch.
 */
export function ButtonGroup({
  children,
  label,
}: {
  children: ReactNode;
  /** Accessible name for the group, e.g. "Report shape". */
  label: string;
}) {
  return (
    <div className="btn-group" role="group" aria-label={label}>
      {children}
    </div>
  );
}

export default Button;
